import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    Tooltip,
    Typography
} from '@material-ui/core';
import { CallSplit } from '@material-ui/icons';
import React from 'react';

import { hasRole } from 'shared/models/user';

import { fetchPersonDetails } from '../actions';
import { splitPerson } from '../api';
import { useReduxDispatch } from '../hooks/use-redux';
import { useSession } from '../hooks/use-session';
import { ProfileDataTransfer } from './profile-data-transfer';

/** Creates an empty person, then lets the user move selected data with the existing transfer tool. */
export const PersonSplit: React.FC<{ personId: string }> = ({ personId }) => {
    const dispatch = useReduxDispatch();
    const { userPermissions } = useSession();
    const [open, setOpen] = React.useState(false);
    const [name, setName] = React.useState('');
    const [newPersonId, setNewPersonId] = React.useState<string>();
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState('');

    if (!hasRole(userPermissions, 'site_owner')) return null;

    const handleOpen = () => {
        setName('');
        setError('');
        setOpen(true);
    };
    const handleClose = () => {
        if (saving) return;
        setOpen(false);
        if (newPersonId) dispatch(fetchPersonDetails(personId));
        setNewPersonId(undefined);
    };
    const handleName = (event: React.ChangeEvent<HTMLInputElement>) => setName(event.target.value);
    const handleCreate = async (event: React.FormEvent) => {
        event.preventDefault();
        if (saving || !name.trim()) return;
        setSaving(true);
        setError('');
        try {
            const result = await splitPerson(personId, name.trim());
            if (!result.success) throw new Error(result.data?.message || 'Unable to create person');
            setNewPersonId(result.data.person.id);
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const dialog = !open ? null : newPersonId ? (
        <ProfileDataTransfer personId1={personId} personId2={newPersonId} onClose={handleClose} />
    ) : (
        <Dialog open={true} onClose={handleClose} maxWidth="sm" fullWidth={true}>
            <form onSubmit={handleCreate}>
                <DialogTitle>
                    <Typography variant="h4" component="div">
                        Split profile
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography>Create a new person, then choose which data to move.</Typography>
                    <TextField
                        label="New person's name"
                        value={name}
                        onChange={handleName}
                        disabled={saving}
                        autoFocus={true}
                        fullWidth={true}
                        margin="normal"
                        autoComplete="off"
                    />
                    <Typography color="error">{error}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button type="submit" disabled={saving || !name.trim()}>
                        {saving ? 'Creating...' : 'Create person'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );

    return (
        <>
            <Tooltip title="Split profile">
                <IconButton
                    className="header-candidate-action"
                    aria-label="Split profile"
                    color="inherit"
                    onClick={handleOpen}
                >
                    <CallSplit />
                </IconButton>
            </Tooltip>
            {dialog}
        </>
    );
};
